import {
  useEffect, useLayoutEffect, useRef, useState
} from 'react'
import { useQueryClient } from 'react-query'
import { useFiltersStateContext } from '../state/useFiltersState'
import { useFiltersQuery } from '../queries/useFiltersQuery'
import { useDatasetsMutation } from '../queries/useDatasetsMutation'
import { prepareFiltersForSubmit, splitKeywords, trackDatasets } from '../utils'
import { useKeywordsStateContext } from '../state/useKeywordsState'
import { TDataset } from '../definitions'
import { DATASETS_QUERY_KEY } from '../queries/useDatasetsQuery'

const useTrackDatasets = () => {
  const [isTrackingAvailable, setIsTrackingAvailable] = useState(false)
  const datasets = useQueryClient().getQueryData<TDataset[]>(DATASETS_QUERY_KEY)

  useEffect(() => {
    // Prevent repeated tracking during the mounting stage
    if (datasets && !isTrackingAvailable) {
      setIsTrackingAvailable(true)
    }
  }, [datasets])

  return (datasets: TDataset[]) => {
    if (isTrackingAvailable) {
      trackDatasets(datasets)
    }
  }
}

export const useDatasetsUpdate = () => {
  const { getSelectedFiltersCount, filters: selectedFilters } = useFiltersStateContext()
  const selectedFiltersCount = getSelectedFiltersCount()
  const { submittedKeywords, includeStemmingSearch } = useKeywordsStateContext()
  const filtersData = useFiltersQuery(false).data
  const { mutateAsync, isLoading } = useDatasetsMutation()
  const trackResponse = useTrackDatasets()
  const hasActiveQuery = Boolean(selectedFiltersCount || submittedKeywords)
  const abortControllerRef = useRef<AbortController | null>(null)
  const [isPending, setIsPending] = useState(() => hasActiveQuery)
  const requestIdRef = useRef(0)
  const isMountedRef = useRef(true)

  useEffect(() => () => {
    isMountedRef.current = false
    abortControllerRef.current?.abort()
  }, [])

  const fetchData = async () => {
    requestIdRef.current += 1
    const requestId = requestIdRef.current

    if (isMountedRef.current) {
      setIsPending(true)
    }
    abortControllerRef.current?.abort()
    abortControllerRef.current = new AbortController()

    try {
      const response = await mutateAsync({
        data: {
          filters: filtersData ? prepareFiltersForSubmit(selectedFilters, filtersData) : [],
          keywords: splitKeywords(submittedKeywords),
          includeStemmingSearch
        },
        signal: abortControllerRef.current.signal
      })
      if (response) {
        trackResponse(response.flatMap(({ dataset }) => dataset))
      }
    } catch (_error) {
      // ignore errors because `useDatasetsMutation` already updates the cache
    } finally {
      if (isMountedRef.current && requestId === requestIdRef.current) {
        setIsPending(false)
      }
    }
  }

  useLayoutEffect(() => {
    if (filtersData?.length && hasActiveQuery) {
      fetchData()
    } else if (isMountedRef.current && !hasActiveQuery) {
      requestIdRef.current += 1
      setIsPending(false)
      abortControllerRef.current?.abort()
    }
  }, [selectedFilters, filtersData, submittedKeywords, hasActiveQuery, includeStemmingSearch])

  return {
    isUpdating: isLoading || isPending,
    abortController: abortControllerRef.current
  }
}
